import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);

  const checkPaymentStatus = useCallback(async () => {
    if (attempts >= 5) {
      setIsLoading(false);
      toast.error("Temps d'attente dépassé. Vérifiez votre email pour la confirmation.");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const status = response.data;

      if (status.payment_status === 'paid') {
        setPaymentStatus(status);
        setIsLoading(false);
        toast.success("Paiement réussi!");
      } else if (status.status === 'expired') {
        setPaymentStatus(status);
        setIsLoading(false);
        toast.error("Session expirée. Veuillez réessayer.");
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(() => checkPaymentStatus(), 2000);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setIsLoading(false);
      toast.error("Erreur lors de la vérification du paiement");
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      navigate('/dashboard');
    }
    // We intentionally omit checkPaymentStatus from deps to run this only once
    // when sessionId is first available (recursive calls handle subsequent polls)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <nav className="bg-white border-b border-border/40 px-4 md:px-8 lg:px-12 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/woma-logo.png"
              alt="ARTEKNOS Logo"
              className="h-12 w-12 rounded-full object-cover ring-2 ring-[#D4AF37]/40 shadow-lg shadow-[#D4AF37]/20"
              data-testid="arteknos-logo"
            />
            <h1 className="text-xl md:text-2xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color: 'hsl(var(--primary))'}}>
              Livraison Express
            </h1>
          </div>
          <div className="flex gap-4">
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-full" data-testid="payment-dashboard-btn">Dashboard</Button>
            <Button onClick={onLogout} variant="outline" className="rounded-full" data-testid="payment-logout-btn">Déconnexion</Button>
          </div>
        </div>
      </nav>

      <div className="px-4 py-16 flex items-center justify-center min-h-[80vh]">
        <Card className="max-w-md w-full shadow-xl">
          <CardHeader>
            <CardTitle className="text-center text-2xl" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="payment-status-title">
              {isLoading ? "Vérification du paiement..." : paymentStatus?.payment_status === 'paid' ? "Paiement réussi!" : "Échec du paiement"}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            {isLoading ? (
              <div className="py-8" data-testid="payment-loading">
                <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Veuillez patienter pendant que nous vérifions votre paiement...</p>
              </div>
            ) : paymentStatus?.payment_status === 'paid' ? (
              <div className="py-8" data-testid="payment-success">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <p className="text-lg mb-2">Votre paiement a été traité avec succès!</p>
                <p className="text-muted-foreground mb-6">Montant: {new Intl.NumberFormat('fr-FR').format(paymentStatus.amount_total)} FCFA</p>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6"
                  data-testid="back-to-dashboard-btn"
                >
                  Retour au tableau de bord
                </Button>
              </div>
            ) : (
              <div className="py-8" data-testid="payment-failed">
                <p className="text-muted-foreground mb-6">Le paiement n'a pas pu être vérifié. Veuillez contacter le support.</p>
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="rounded-full"
                  data-testid="back-to-dashboard-failed-btn"
                >
                  Retour au tableau de bord
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
